import { useState, useEffect, useMemo } from 'react';
import { getKeys } from 'shared/lib/object';
import type { IntegrationModel } from 'entities/integration';
import { isString } from 'shared/lib/type-guards';
import type { IntegrationFieldsModel } from 'entities/campaign-integrations';

import type { IntegrationFieldsListForm } from './IntegrationFieldsList';

export type KeysStore = Array<Field> | Array<Field & MetaData>;

export function getArrayForIntegrationsFieldsList(fields: IntegrationFieldsModel) {
  return getKeys(fields).reduce<KeysStore>((previousValue, currentValue) => {
    return [
      ...previousValue,
      {
        key: currentValue,
        value: fields[currentValue],
      },
    ];
  }, []);
}

export type Field = {
  key: string;
  value: string;
};

type UseIntegrationFieldsParams = {
  integrationFields?: IntegrationFieldsListForm['fields'];
  integrationData?: IntegrationModel[];
  isActive?: boolean;
  integrationAlias?: string;
};

export type MetaData = {
  deleted: boolean;
  touched: boolean;
};

export const removeMeta = (fields: Array<Field & MetaData>): IntegrationFieldsListForm['fields'] => {
  return fields.map((field) => ({
    key: field.key,
    value: field.value,
  }));
};

const ADD_SIGN = '@@+';
const REMOVE_SIGN = '@@-';

export function isFieldWithMetaArray(
  fields: IntegrationFieldsListForm['fields'] | Array<Field & MetaData>
): fields is Array<Field & MetaData> {
  return Array.isArray(fields) && fields.every((field) => isFieldWithMeta(field));
}

export function isFieldWithMeta(field: Field | (Field & MetaData)): field is Field & MetaData {
  return typeof field.key === 'string' && typeof (field as Field & MetaData).touched === 'boolean';
}

export const convertMetaToData = (
  fields: IntegrationFieldsListForm['fields'] | Array<Field & MetaData>
): IntegrationFieldsListForm['fields'] => {
  if (isFieldWithMetaArray(fields)) {
    return fields.map((field) => {
      if (field.touched) {
        if (field.deleted) {
          return {
            ...field,
            key: `${REMOVE_SIGN}${field.key}`,
          };
        }

        return {
          ...field,
          key: `${ADD_SIGN}${field.key}`,
        };
      }

      return field;
    });
  }

  return fields;
};

const INTEGRATION_WITH_META_SIGN = 'crm_constructor';

const getHasMeta = (integration: IntegrationModel | undefined, integrationAlias?: string) => {
  if (integrationAlias) {
    return integrationAlias.startsWith(INTEGRATION_WITH_META_SIGN);
  }
  if (integration) {
    return integration.alias.startsWith(INTEGRATION_WITH_META_SIGN);
  }

  return false;
};

const extractMeta = (fields: IntegrationFieldsListForm['fields']) => {
  return fields.map((field) => {
    if (field.key.startsWith(ADD_SIGN)) {
      return {
        ...field,
        key: field.key.slice(ADD_SIGN.length),
        touched: true,
        deleted: false,
      };
    }

    if (field.key.startsWith(REMOVE_SIGN)) {
      return {
        ...field,
        key: field.key.slice(REMOVE_SIGN.length),
        touched: true,
        deleted: true,
      };
    }

    return {
      ...field,
      touched: false,
      deleted: false,
    };
  });
};

export const useIntegrationFields = ({
  integrationData,
  isActive,
  integrationAlias,
  integrationFields,
}: UseIntegrationFieldsParams) => {
  const [integrationId, setIntegrationId] = useState('');
  const [doNotActivateOnAdd, setDoNotActivateOnAdd] = useState(isActive ?? false);
  const integration = integrationData ? integrationData.find((x) => x.id === Number(integrationId)) : undefined;

  const hasMeta = getHasMeta(integration, integrationAlias);
  const [fields, setFields] = useState<IntegrationFieldsListForm['fields'] | Array<Field & MetaData>>([]);

  const [errors, setErrors] = useState<Record<number, string>>({});

  const [isDirty, setIsDirty] = useState(false);

  const isDisabled = !fields.length;

  const integrationTemplate = useMemo(() => {
    if (integration) {
      const fieldsFromIntegration = getKeys(integration.fields).reduce((prev, curr) => {
        return {
          ...prev,
          [curr]: isString(integration.fields[curr]) ? integration.fields[curr] : '',
        };
      }, {});

      return hasMeta
        ? extractMeta(getArrayForIntegrationsFieldsList(fieldsFromIntegration))
        : getArrayForIntegrationsFieldsList(fieldsFromIntegration);
    }

    if (integrationFields) {
      return hasMeta ? extractMeta(integrationFields) : integrationFields;
    }

    return [];
  }, [integration, integrationFields]);

  useEffect(() => {
    setFields(integrationTemplate);
  }, [integrationTemplate]);

  const onChange = (idx: number, key: string, value: string) => {
    setIsDirty(true);
    setFields((prevFields) =>
      prevFields.map((item, prevIdx) => {
        if (prevIdx === idx) {
          return { ...item, key, value };
        }

        return item;
      })
    );
  };

  const append = (field: Field) =>
    setFields((prevFields) => {
      if (isFieldWithMetaArray(prevFields)) {
        if (integrationTemplate.length && integrationTemplate.some((initialField) => initialField.key === field.key)) {
          return [...prevFields, { ...field, touched: false, deleted: false }];
        }

        return [...prevFields, { ...field, touched: true, deleted: false }];
      }

      return [...prevFields, field];
    });

  const remove = (idx: number) => {
    setFields((prevFields) => {
      const keyToDelete = prevFields[idx].key;
      const isInInitialTemplate = integrationTemplate.some((initialField) => initialField.key === keyToDelete);
      const hasDuplicate = prevFields.filter((prevField) => prevField.key === keyToDelete).length > 1;

      if (isFieldWithMetaArray(prevFields)) {
        if (prevFields[idx].touched && prevFields[idx].deleted) {
          return prevFields.map((field, currIdx) => {
            if (idx === currIdx) {
              return {
                ...field,
                touched: false,
                deleted: false,
              };
            }

            return field;
          });
        }
      }

      if (isFieldWithMetaArray(prevFields) && isInInitialTemplate && !hasDuplicate) {
        return prevFields.map((field, currIdx) => {
          if (idx === currIdx) {
            return {
              ...field,
              touched: true,
              deleted: true,
            };
          }

          return field;
        });
      }

      return [...prevFields.slice(0, idx), ...prevFields.slice(idx + 1)];
    });
  };
  const validate = () => {
    let currentErrors: Record<number, string> = {};
    let seen = new Set<string>();

    fields.forEach((field, idx) => {
      if (!field.key.trim()) {
        currentErrors = { ...currentErrors, [idx]: 'This field is required' };
      }

      if (seen.has(field.key)) {
        currentErrors = { ...currentErrors, [idx]: 'This key has been added already' };
      }
      seen.add(field.key);
    });

    setErrors(currentErrors);

    const hasError = getKeys(currentErrors).length > 0;

    return { hasError };
  };

  return {
    errors,
    setErrors,
    fields,
    isDirty,
    isDisabled,
    setDoNotActivateOnAdd,
    doNotActivateOnAdd,
    setIsDirty,
    onChange,
    remove,
    append,
    setIntegrationId,
    integration,
    integrationId,
    validate,
    hasMeta,
  };
};
